// ───────────────────────────────────────────────────────────────────
// Shared product-analytics taxonomy (Firebase Analytics on mobile, GA4 on
// web-employer). Event + property NAMES live here so the worker app and the
// employer app emit an identical vocabulary — the cross-side marketplace
// funnel (apply → invite → commit → interview) only joins if both sides use
// the same strings.
//
// GA4/Firebase constraints respected here:
//   - event name  ≤ 40 chars, snake_case, starts with a letter
//   - param name  ≤ 40 chars
//   - user-property name ≤ 24 chars, value ≤ 36 chars
//
// Privacy: NEVER put email / phone / CCCD / full name into a param or
// property. Only internal ids (workerId, orgId, postingId, inviteId) and
// coarse buckets. PII as analytics params violates GA4 policy.
// ───────────────────────────────────────────────────────────────────

/** Backbone events fired from the worker mobile app. */
export const WorkerAnalyticsEvent = {
  /** OTP verified successfully — the activation moment for a worker. */
  otpVerified: "otp_verified",
  /** Worker crossed the minimum (L1) profile bar that unlocks the feed. */
  profileCompleted: "profile_completed",
  /** KYC submission accepted by the backend (status PENDING/…). */
  kycSubmitted: "kyc_submitted",
  /** Worker applied to a posting — the worker-side core action. */
  applicationSubmitted: "application_submitted",
  /** Worker accepted an invite and picked a slot (enters commit window). */
  slotAccepted: "slot_accepted",
  /** Invite reached COMMITTED (contact revealed) — the north-star event. */
  interviewCommitted: "interview_committed",
  /** Worker flipped the "Đang tìm việc" (open-to-work) toggle. */
  openToWorkToggled: "open_to_work_toggled",
} as const;

/** Backbone events fired from the employer web app. */
export const EmployerAnalyticsEvent = {
  /** Employer account created. */
  employerSignup: "employer_signup",
  /** Organization created during onboarding. */
  orgCreated: "org_created",
  /** Job posting published — the employer-side core action. */
  postingPublished: "posting_published",
  /** Employer invited an applicant (source: invite_dialog | quick_review | talent_deck). */
  applicantInvited: "applicant_invited",
  /** Employer opened the "Tìm ứng viên" talent deck. */
  talentFeedViewed: "talent_feed_viewed",
  /** Employer skipped a candidate card in the talent deck. */
  talentCardSkipped: "talent_card_skipped",
  /** Interview-token purchase confirmed paid + credited. */
  creditPurchaseCompleted: "credit_purchase_completed",
} as const;

export const AnalyticsEvent = {
  ...WorkerAnalyticsEvent,
  ...EmployerAnalyticsEvent,
} as const;

export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

/** User-property names (≤24 chars). Set once identity is known; used to cut
 *  every report by segment instead of staring at a single total. */
export const AnalyticsUserProperty = {
  role: "role", // "worker" | "employer"
  city: "city",
  // worker
  kycStatus: "kyc_status",
  profileBucket: "profile_bucket", // 0_49 | 50_79 | 80_99 | complete
  desiredPosition: "desired_position", // primary desired position
  // employer
  orgId: "org_id",
  venueType: "venue_type",
  isPaying: "is_paying", // "true" | "false" — has ever bought tokens
} as const;

/** A value safe to send to Firebase/GA4 (no objects, no PII). */
export type AnalyticsParamValue = string | number | boolean | undefined | null;
export type AnalyticsParams = Record<string, AnalyticsParamValue>;

/** Coarse profile-completion bucket for the `profile_bucket` user property. */
export function profileCompletionBucket(
  pct: number | null | undefined,
): "0_49" | "50_79" | "80_99" | "complete" {
  const p = pct ?? 0;
  if (p >= 100) return "complete";
  if (p >= 80) return "80_99";
  if (p >= 50) return "50_79";
  return "0_49";
}
